import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { EMPTY_STRING, loginReducerFlags, stepPath } from "../../constants";
import PropTypes from "prop-types";
import {
    setAuthToken,
    setFlag,
    setUserData,
    setUsername,
    setUserUUID,
} from "./actions";
import { getUser } from "./helpers";
import { withAuth0 } from "@auth0/auth0-react";
import SpinLoader from "../__shared__/SpinLoader";
import { addPendoTracking } from "../../helpers/helpers";
import { trackingEventNames } from "../../helpers/pendo_constants";

class Login extends Component {
    constructor(props) {
        super(props);
    }

    async componentDidMount() {
        const isExpectingRedirectFromSSO = sessionStorage.getItem(
            "isExpectingRedirectFromSSO"
        );

        if (isExpectingRedirectFromSSO === "true") {
            // Prevent silent login loop by removing the flag
            sessionStorage.removeItem("isExpectingRedirectFromSSO");

            try {
                const token = await this.getAuth0Token();
                if (!token) {
                    throw new Error("Failed to get Auth0 token.");
                }
                await this.handleLoginSuccess(token);
            } catch (error) {
                console.error("Error in getAuth0Token: ", error);
                // If there is an error getting the token, redirect to login
                this.redirectToLogin();
            }
        }

        // Sign Up -> Login flow will not go through componentDidUpdate,
        // so redirect flow needs to be handled here
        else if (!this.props.auth0.isAuthenticated) {
            this.redirectToLogin();
        }
    }

    async componentDidUpdate(prevProps) {
        if (this.props.auth0.isLoading) {
            return; // wait until Auth0 is loaded
        }

        // This check is for when user is completely logged out
        // Now redirect them to the SSO Login page
        if (
            prevProps.auth0.isAuthenticated &&
            !this.props.auth0.isAuthenticated
        ) {
            this.redirectToLogin();
        }
    }

    redirectToLogin() {
        sessionStorage.setItem("isExpectingRedirectFromSSO", "true");

        // Please see documentation found here for scope definitions:
        // https://auth0.com/docs/get-started/apis/scopes/openid-connect-scopes
        this.props.auth0.loginWithRedirect({
            authorizationParams: {
                audience: import.meta.env.VITE_AUTH0_AUDIENCE,
                scope: "openid profile email offline_access",
            },
        });
    }

    async getAuth0Token() {
        try {
            return await this.props.auth0.getAccessTokenSilently();
        } catch (error) {
            console.error("Error getting Auth0 token: ", error);
        }
    }

    async handleLoginSuccess(token) {
        if (this.props.flags.isAuthenticated) {
            return;
        }

        this.props.setAuthToken(token);

        const isSSOUser = this.isSSOUser(this.props.auth0.user.sub);
        this.props.setFlag({
            flagId: loginReducerFlags.IS_SSO_USER,
            newFlagState: isSSOUser,
        });

        await getUser(this.props.auth0.user.email, token)
            .then((result) => {
                this.props.setUserUUID(result.user.uuid);
                this.props.setUserData(result.user);

                // Set the username in the Redux store
                this.props.setUsername(this.props.auth0.user.email);

                // The software redirects user from Login to Home when this flagId is updated in the Redux store
                this.props.setFlag({
                    flagId: loginReducerFlags.IS_AUTHENTICATED,
                    newFlagState: true,
                });

                // Navigate to home after successful login
                this.props.history.push(stepPath.HOME);
            })
            .catch((e) => {
                // The user is not found in our records, redirect them to "Contact IT" page for help
                addPendoTracking(
                    trackingEventNames.LOGIN,
                    null,
                    "User Not Found"
                );
                console.log("User not found in our records: ", e);
                this.props.history.push(stepPath.CONTACT_IT);
            });

        // Pendo tracking
        if (window.pendo) {
            try {
                window.pendo.identify({
                    visitor: {
                        id: this.props.userData.uuid,
                        email: this.props.userData.email,
                        firstName: this.props.userData.firstName,
                        lastName: this.props.userData.lastName,
                        mfaEnabled: this.props.userData.mfaEnabled,
                    },

                    account: {
                        id: this.props.userData?.institutionID
                            ? this.props.userData.institutionID
                            : "ACCOUNT-UNIQUE-ID",
                    },
                });
            } catch (error) {
                console.error(
                    "Error retrieving user information for Pendo:",
                    error
                );
            }
        }
    }

    isSSOUser(userSub) {
        return userSub.split("|")[0] == "samlp";
    }

    render() {
        return (
            <div
                className={
                    "signup-screen flex h-screen w-screen items-center justify-center"
                }
            >
                <SpinLoader
                    width={"50px"}
                    height={"50px"}
                    display={"block"}
                    loaderWidth={"3px"}
                    fgColor="var(--avendaTeal1)"
                />
            </div>
        );
    }
}

Login.propTypes = {
    unText: PropTypes.string,
    pwText: PropTypes.string,
    userData: PropTypes.object,
    flags: PropTypes.object,
    history: PropTypes.object,
    setUserUUID: PropTypes.func,
    setAuthToken: PropTypes.func,
    setFlag: PropTypes.func,
    setUserData: PropTypes.func,
    setUsername: PropTypes.func,
    auth0: PropTypes.object,
};

Login.defaultProps = {
    userData: {},
    unText: EMPTY_STRING,
};

/* istanbul ignore next */
const mapStateToProps = function (state) {
    return {
        userData: state.LoginReducer.userData,
        unText: state.LoginReducer.unText,
        pwText: state.LoginReducer.pwText,
        flags: state.LoginReducer.flags,
    };
};

/* istanbul ignore next */
const mapDispatchToProps = function (dispatch) {
    return {
        setUserData: (userData) => dispatch(setUserData(userData)),
        setFlag: (flag) => dispatch(setFlag(flag)),
        setAuthToken: (token) => dispatch(setAuthToken(token)),
        setUsername: (text) => dispatch(setUsername(text)),
        setUserUUID: (uuid) => dispatch(setUserUUID(uuid)),
    };
};

export default withAuth0(
    withRouter(connect(mapStateToProps, mapDispatchToProps)(Login))
);
